import * as Sentry from '@sentry/sveltekit';
import type { User } from '$lib/types/user';

/**
 * Opens the Sentry feedback dialog with user context pre-populated
 * @param user - The current user object (optional)
 * @param loggedIn - Whether the user is logged in (optional)
 * @returns Promise<boolean> - Returns true if feedback dialog opened successfully, false otherwise
 */
export async function openSentryFeedback(user?: User | null, loggedIn?: boolean): Promise<boolean> {
	try {
		// Set Sentry user context first (this pre-populates the feedback form)
		if (user && loggedIn) {
			const firstName = user.firstName || '';
			const lastName = user.lastName || '';
			const fullName = `${firstName} ${lastName}`.trim();

			// Set user context in Sentry - this is what pre-populates the feedback form
			Sentry.setUser({
				id: user.id.toString(),
				email: user.email,
				username: fullName, // This maps to the name field
			});
		}

		// Get feedback integration through the client (more reliable than Sentry.getFeedback())
		const client = Sentry.getClient();
		if (client) {
			const clientWithOptions = client as { getOptions?: () => { integrations?: unknown[] } };
			const allIntegrations = clientWithOptions.getOptions?.()?.integrations || [];
			const feedbackIntegration = allIntegrations.find((integration: unknown) => {
				const integrationObj = integration as { name?: string; constructor?: { name?: string } };
				const name = integrationObj.name || integrationObj.constructor?.name || '';
				return name.toLowerCase().includes('feedback');
			}) as
				| { createForm?: () => Promise<{ appendToDom: () => void; open: () => void } | null> }
				| undefined;

			if (feedbackIntegration && feedbackIntegration.createForm) {
				const form = await feedbackIntegration.createForm();
				if (form) {
					form.appendToDom();
					form.open();
					return true;
				}
			}
		}

		// Fallback to original method (in case the above doesn't work in some versions)
		const feedback = Sentry.getFeedback && Sentry.getFeedback();
		if (feedback) {
			const form = await feedback.createForm();
			if (form) {
				form.appendToDom();
				form.open();
				return true;
			}
		}

		console.warn('Sentry feedback integration not available');
		return false;
	} catch (error) {
		console.error('Error opening feedback dialog:', error);
		return false;
	}
}

/**
 * Shows a user-friendly error message when feedback fails to open
 * @param message - Optional custom error message
 */
export function showFeedbackError(message?: string): void {
	const defaultMessage =
		'Unable to open feedback form. Please try again later or contact support directly.';
	const errorMessage = message || defaultMessage;

	// Create a temporary toast-like notification instead of alert
	const notification = document.createElement('div');
	notification.style.cssText = `
		position: fixed;
		top: 20px;
		right: 20px;
		background: #dc3545;
		color: white;
		padding: 12px 16px;
		border-radius: 4px;
		font-size: 14px;
		z-index: 10000;
		max-width: 300px;
		box-shadow: 0 2px 8px rgba(0,0,0,0.2);
	`;
	notification.textContent = errorMessage;

	document.body.appendChild(notification);

	// Remove notification after 5 seconds
	setTimeout(() => {
		if (notification.parentNode) {
			notification.parentNode.removeChild(notification);
		}
	}, 5000);
}
